import _ from 'lodash'
import { get, flow, keyBy, filter, includes, flatten, eq, find } from 'lodash/fp'

import defaults from 'core/data-model/constants/defaults'
import riderValidationMessages from 'core/data-model/constants/validation-messages'
import values from 'core/data-model/constants/values'
import { isAgeLessThanOrEqual, isAgeMoreThanOrEqual } from 'core/service/insured'
import { transformOccupationFactor } from 'core/service/insured/occupation'
import { isCurrentDateInRange } from 'core/service/lib/date'
import { isCompatibleWithSelectedRiders, checkCompatibilities } from 'core/service/rider/rider-compatibilities'
import { checkRules } from 'core/service/rider/rules/rider-rules'
import { getToggles } from 'quick-quote/feature-toggles'

export const isRequirePayerInfo = (riderCode) => includes(riderCode)(defaults.RIDERS_REQUIRE_PAYER_INFO)

export const isValidPayerRelation = (rider, payerRelation) => {
  return (
    payerRelation === '' ||
    _.isUndefined(payerRelation) ||
    _.isUndefined(rider.payerRelation) ||
    rider.payerRelation.length === 0 ||
    rider.payerRelation.includes(payerRelation)
  )
}

export const isRiderSelectable = (rider, riderEligibilities) => {
  return (
    checkRules(rider, riderEligibilities, values.ELIGIBILITY).isOk &&
    isValidPayerRelation(rider, riderEligibilities.payerRelation)
  )
}

async function getRiderInstance() {
  const AP = require('core/service/rider/rider-ap').default
  const CIES = require('core/service/rider/rider-cies').default
  const CIUDR = require('core/service/rider/rider-ciudr').default
  const CPR = require('core/service/rider/rider-cpr').default
  const DCI = require('core/service/rider/rider-dci').default
  const ECARE = require('core/service/rider/rider-ecare').default
  const ESCI = require('core/service/rider/rider-esci').default
  const HBUDR = require('core/service/rider/rider-hbudr').default
  const HIC = require('core/service/rider/rider-hic').default
  const HSUDR = require('core/service/rider/rider-hsudr').default
  const MCI = require('core/service/rider/rider-mci').default
  const MEAPlus = require('core/service/rider/rider-meaplus').default
  const MEB = require('core/service/rider/rider-meb').default
  const MEX = require('core/service/rider/rider-mex').default
  const MHP = require('core/service/rider/rider-mhp').default
  const MRTAAP = require('core/service/rider/rider-mrtaap').default
  const PARIDER = require('core/service/rider/rider-parider').default
  const PBPDD = require('core/service/rider/rider-pbpdd').default
  const PBPDDCI = require('core/service/rider/rider-pbpddci').default
  const PBSDD = require('core/service/rider/rider-pbsdd').default
  const PBSDDCI = require('core/service/rider/rider-pbsddci').default
  const PLR = require('core/service/rider/rider-plr').default
  const TPDUDR = require('core/service/rider/rider-tpdudr').default
  const WP = require('core/service/rider/rider-wp').default
  const WPD = require('core/service/rider/rider-wpd').default
  const WPDD = require('core/service/rider/rider-wpdd').default
  const WPEIP85 = require('core/service/rider/rider-wpeip85').default
  const WPTPD = require('core/service/rider/rider-wptpd').default
  const WPTPDCI = require('core/service/rider/rider-wptpdci').default

  return {
    AP,
    CIES,
    CIUDR,
    CPR,
    DCI,
    ECARE,
    ESCI,
    HBUDR,
    HIC,
    HSUDR,
    MCI,
    MEAPlus,
    MEB,
    MEX,
    MHP,
    MRTAAP,
    PARIDER,
    PBPDD,
    PBPDDCI,
    PBSDD,
    PBSDDCI,
    PLR,
    PLS: PLR,
    TPDUDR,
    WP,
    WPD,
    WPDD,
    WPEIP85,
    WPTPD,
    WPTPDCI,
  }
}

export const isSelectedPlanShift = (rider, insuredAge, riderSelectedPlan) => {
  const maxJuvenileAge = get('juvenilePolicy.maxAge')(rider)
  if (rider.packageRidersGroup !== undefined && riderSelectedPlan && insuredAge) {
    if (insuredAge.value <= maxJuvenileAge && !_.find(rider.packageRidersGroup, { plan: riderSelectedPlan.plan })) {
      return true
    } else if (insuredAge.value > maxJuvenileAge && !_.find(rider.packageRiders, { plan: riderSelectedPlan.plan })) {
      return true
    }
  }
  return false
}

export const getOptionValueFromList = (rider, insuredAge) => {
  const maxJuvenileAge = get('juvenilePolicy.maxAge')(rider)
  if (insuredAge && insuredAge.value <= maxJuvenileAge) {
    const defaultPackage = flow(get('options.0.values'), find({ view: 'juvenile' }))(rider)
    const selectedplanGroups = _.get(rider, 'packageRidersGroup', [])
      .find((p) => p.plan === defaultPackage.plan)
      .planGroups.find((p) => p.isSelected)

    const selectedModules = _.get(selectedplanGroups, 'modules', []).map((m) => ({
      code: m.code[defaultPackage.areaOfCover],
      description: m.description,
      isSelected: true,
    }))
    return { ...defaultPackage, modules: selectedModules }
  } else return flow(get('options.0.values'), find({ view: 'adult' }))(rider)
}

const toYears = (unit, value) => {
  if (unit === 'year') return value
  if (unit === 'month') return value / 12
  return value
}

export const isAgeAllowed = (insureAgeYears, allowAge) => {
  if (!allowAge) return true
  const minOk = allowAge.minimumValue
    ? insureAgeYears >= toYears(allowAge.minimumValue.unit, allowAge.minimumValue.value)
    : true
  const maxOk = allowAge.maximumValue
    ? insureAgeYears <= toYears(allowAge.maximumValue.unit, allowAge.maximumValue.value)
    : true
  return minOk && maxOk
}

const getFirstEligible = (values, insureAgeYears) => {
  return values.find((v) => isAgeAllowed(insureAgeYears, v.allowAge)) ?? values[0]
}

const getDefaultRiderOption = (rider, defaultRiderPlanCode, insureAgeYears) => {
  const values = get('options.0.values')(rider) ?? []
  if (!Array.isArray(values) || values.length === 0) return undefined

  if (!defaultRiderPlanCode) {
    return getFirstEligible(values, insureAgeYears)
  }

  const found = find({ planCode: defaultRiderPlanCode })(values)

  if (!found) {
    return getFirstEligible(values, insureAgeYears)
  }

  if (!found.allowAge) {
    return found
  }

  const result = isAgeAllowed(insureAgeYears, found.allowAge) ? found : getFirstEligible(values, insureAgeYears)
  return result
}

export const getSelectedRiderPlanByPayerOccupationFactor = (
  rider,
  basicPlan,
  basicSumAssured,
  occupationFactor,
  payerOccupationFactor
) => {
  const riderOptionValues = get('options.0.values')(rider)
  const riderPlanCode =
    flow(
      filter((v) => v.source === values.SOURCE_RIDER_PAYER_OCCUPATIONFACTOR),
      keyBy('value'),
      get(payerOccupationFactor),
      get('planCode')
    )(riderOptionValues) || get('0.planCode')(riderOptionValues)
  return { planCode: riderPlanCode }
}

export const getSelectedRiderPlanByProductPlan = (rider, basicPlan, basicSumAssured, userAgentType) => {
  const riderOptionValues = get('options.0.values')(rider)
  const riderSumAssured = getRiderSumAssuredByProductPlan(rider, basicSumAssured, basicPlan, userAgentType)
  const riderPlanCode =
    flow(
      filter((v) => v.source === values.SOURCE_RIDER_SUM_ASSURED),
      keyBy('value'),
      get(riderSumAssured),
      get('planCode')
    )(riderOptionValues) || ''
  return { planCode: riderPlanCode }
}

export const getRiderSumAssuredByProductPlan = (rider, basicSumAssured, basicPlan, userAgentType) => {
  const productPlanSource = get('productPlans.source')(basicPlan)
  const productPlans = get('productPlans.values')(basicPlan)

  switch (productPlanSource) {
    case values.SOURCE_BASIC_SUM_ASSURED:
      const sumAssured = flow(
        flatten,
        find((plan) => {
          return (
            flow(get('plan'), eq(basicSumAssured))(plan) && flow(get('allowUserType'), includes(userAgentType))(plan)
          )
        }),
        get('ridersOverrides'),
        get(rider.code),
        get('sumAssured')
      )(productPlans)

      return sumAssured ? sumAssured : rider.defaultSumAssured
    default:
      return rider.sumAssured ? rider.sumAssured : rider.defaultSumAssured
  }
}

export const getValidationMessages = (rider, riderSumAssured, riderEligibilities, selectedRiders) => {
  let validationMessages = checkRules(rider, riderEligibilities).messages
  let compatibilityMessages = checkCompatibilities(rider, selectedRiders).messages
  let invalidPayerMessages = isValidPayerRelation(rider, riderEligibilities.payerRelation)
    ? []
    : [
        {
          message: riderValidationMessages.RIDER_INVALID_PAYER,
          type: 'ELIGIBILITY',
        },
      ]

  return _.union(_.uniqBy(validationMessages, 'message'), compatibilityMessages, invalidPayerMessages)
}

export const hasRidersNeedToCalculateFromTotalPremiumOrAnnualPremium = (riders) => {
  return riders.some((rider) => isRiderNeedToCalculateFromTotalPremiumOrAnnualPremium(rider))
}

export const hasRidersNeedToCalculateFromAnnualTotalPremium = (riders) => {
  return riders.some((rider) => isRiderNeedToCalculateFromTotalAnnualPremium(rider))
}

export const isRiderNeedToCalculateFromTotalPremiumOrAnnualPremium = (rider) => {
  const source = get('options.0.source')(rider)
  return source === values.SOURCE_TOTAL_PREMIUM || source === values.SOURCE_TOTAL_ANNUAL_PREMIUM
}

export const isRiderNeedToCalculateFromTotalAnnualPremium = (rider) => {
  return get('options.0.source')(rider) === values.SOURCE_TOTAL_ANNUAL_PREMIUM
}

export const isRiderBelongToRpudr = (rider) => {
  return [values.CIUDR, values.TPDUDR, values.HBUDR, values.HSUDR].includes(rider.code)
}

export const isCheckRiderCampaignDiscountActive = (rider, userAgentType) => {
  const campaignDiscount = _.get(rider, 'campaignDiscount')
  if (campaignDiscount) {
    const isRiderCampaignDiscountDateRangeActive = getToggles().OUT_OF_DATE_RIDER_CAMPAIGN_DISCOUNT
      ? isCurrentDateInRange('01/01/2025', '01/02/2025')
      : isCurrentDateInRange(_.get(campaignDiscount, 'startDate'), _.get(campaignDiscount, 'endDate'))

    const isRiderCampaignDiscountUserTypeActive = campaignDiscount.allowUserType
      ? campaignDiscount.allowUserType.includes(userAgentType)
      : true

    const isRiderCampaignDiscountOccupationFactorActive = campaignDiscount.validRiderOccupationFactors
      ? campaignDiscount.validRiderOccupationFactors
          .map(transformOccupationFactor)
          .includes(Number(rider.riderOccupationFactor))
      : true

    return (
      isRiderCampaignDiscountDateRangeActive &&
      isRiderCampaignDiscountUserTypeActive &&
      isRiderCampaignDiscountOccupationFactorActive
    )
  } else {
    return false
  }
}

/**
 * @param displayProductService {ReturnType<import('core/service/display-product/display-product').createDisplayProductService>}
 * @param insuredOccupationService {ReturnType<import('core/service/insured/occupation').createInsuredOccupationService>}
 */
export async function createRiderService(displayProductService, insuredOccupationService) {
  const riders = await getRiderInstance()

  const isImplementedRider = (rider) => _.has(riders, rider.code)

  const getYearsOfCoverage = (
    rider,
    insuredAge,
    payerAge,
    basicPaymentPeriod,
    basicPlanCoveragePeriod,
    selectedPlan
  ) => {
    if (!isImplementedRider(rider)) return 0
    const riderCoveragePeriod = rider.coveragePeriod ? rider.coveragePeriod.value : 0
    const riderPaymentPeriod = rider.paymentPeriod ? rider.paymentPeriod.value : 0
    switch (rider.code) {
      case 'PBG':
        return payerAge.value === defaults.RIDER_PBG_MAX_PAYER_AGE
          ? defaults.RIDER_PBG_COVERAGE_MIN
          : defaults.RIDER_PBG_COVERAGE_MAX
      case 'WPD':
        return riders.WPD.getYearsOfCoverage(basicPaymentPeriod.value, insuredAge)
      case 'WPDD':
        return riders.WPDD.getYearsOfCoverage(basicPaymentPeriod.value, insuredAge)
      case 'PLS':
        return riders.PLR.getYearsOfCoverage(selectedPlan)
      case 'PLR':
        return riders.PLR.getYearsOfCoverage(selectedPlan)
      case 'WP':
        return riders[rider.code].getYearsOfCoverage(
          riderCoveragePeriod,
          insuredAge,
          basicPaymentPeriod.value,
          basicPlanCoveragePeriod.value
        )
      case 'WPTPD':
      case 'WPTPDCI':
        return riders[rider.code].getYearsOfCoverage(
          riderCoveragePeriod,
          insuredAge,
          basicPaymentPeriod,
          basicPlanCoveragePeriod.value,
          riderPaymentPeriod
        )
      case 'PBPDD':
      case 'PBPDDCI':
        return riders[rider.code].getYearsOfCoverage(basicPaymentPeriod, insuredAge, payerAge, riderPaymentPeriod)
      case 'PBSDD':
      case 'PBSDDCI':
        return riders[rider.code].getYearsOfCoverage(basicPaymentPeriod, insuredAge, payerAge, riderPaymentPeriod)
      case 'MEH':
        return riders.MEH.getYearsOfCoverage(basicPlanCoveragePeriod, insuredAge.value)
      case 'FH':
        return riders.FH.getYearsOfCoverage(basicPlanCoveragePeriod, insuredAge.value)
      case 'MRTAAP':
        return riders.MRTAAP.getYearsOfCoverage(selectedPlan)
      default:
        const ageValue = insuredAge.unit !== 'year' ? 0 : insuredAge.value
        const yearsOfCoverage = riderCoveragePeriod - ageValue
        return yearsOfCoverage > basicPlanCoveragePeriod.value ? basicPlanCoveragePeriod.value : yearsOfCoverage
    }
  }

  const getYearsOfPayment = (
    rider,
    insuredAge,
    payerAge,
    basicPaymentPeriod,
    basicPlanCoveragePeriod,
    selectedPlan
  ) => {
    if (!isImplementedRider(rider)) return 0

    switch (rider.code) {
      case 'PBG':
        return payerAge.value === defaults.RIDER_PBG_MAX_PAYER_AGE
          ? defaults.RIDER_PBG_COVERAGE_MIN
          : defaults.RIDER_PBG_COVERAGE_MAX
      case 'MEH':
        return riders.MEH.getYearsOfPayment(basicPaymentPeriod, insuredAge.value)
      case 'FH':
        return riders.FH.getYearsOfPayment(basicPaymentPeriod, insuredAge.value)
      default:
        if (_.includes(defaults.PROXY_RIDERS, rider.code)) {
          return _.get(basicPaymentPeriod, 'value')
        } else {
          return getYearsOfCoverage(
            rider,
            insuredAge,
            payerAge,
            basicPaymentPeriod,
            basicPlanCoveragePeriod,
            selectedPlan
          )
        }
    }
  }

  const getSelectedRiderPlanByInsuredAgeWithPlanGroup = (rider, insuredAge) => {
    const packageRiders = _.get(rider, 'packageRiders')
    if (packageRiders && insuredAge) {
      const matchPlan = packageRiders.find((plan) => {
        const minAge = _.get(plan, 'allowAge.minimumValue')
        const maxAge = _.get(plan, 'allowAge.maximumValue')
        return isAgeMoreThanOrEqual(insuredAge, minAge) && isAgeLessThanOrEqual(insuredAge, maxAge)
      })

      const riderSelectedPlan = matchPlan ? _.get(matchPlan, 'planGroupCode') : _.get(rider, 'defaultRiderPlanCode')
      return { planCode: riderSelectedPlan }
    }

    return { planCode: '' }
  }

  const getSelectedRiderPlan = (
    rider,
    riderSelectedPlan,
    basicPlan,
    basicSumAssured,
    userAgentType = defaults.DEFAULT_AGENT_TYPE,
    occupationFactor,
    payerOccupationFactor,
    insuredAge
  ) => {
    if (!isImplementedRider(rider)) return { planCode: '' }
    const riderOptionType = get('options.0.type')(rider)
    const riderOptionSource = get('options.0.source')(rider)

    const isPlanShift = isSelectedPlanShift(rider, insuredAge, riderSelectedPlan)

    if (
      riderSelectedPlan &&
      riderSelectedPlan.planCode !== '' &&
      riderOptionType !== values.OPTION_FIXED_VALUE_FROM_LIST &&
      !isPlanShift
    ) {
      return riderSelectedPlan
    }

    if (rider.package && rider.packageRiders) {
      return { planCode: get('packageRiders.0')(rider) || '' }
    }

    switch (riderOptionType) {
      case values.OPTION_VALUE_FROM_LIST:
        const defaultRiderPlanCode = get('defaultRiderPlanCode')(rider)
        const result = getDefaultRiderOption(rider, defaultRiderPlanCode, insuredAge.value)
        return result
      case values.OPTION_MULTI_VALUE_FROM_LIST:
        return getOptionValueFromList(rider, insuredAge)
      case values.OPTION_FIXED_VALUE_FROM_LIST:
        if (riderOptionSource === values.SOURCE_PRODUCT_PLAN) {
          return getSelectedRiderPlanByProductPlan(rider, basicPlan, basicSumAssured, userAgentType)
        } else if (
          riderOptionSource === values.SOURCE_TOTAL_PREMIUM ||
          riderOptionSource === values.SOURCE_TOTAL_ANNUAL_PREMIUM
        ) {
          return getSelectedRiderPlanByPayerOccupationFactor(
            rider,
            basicPlan,
            basicSumAssured,
            occupationFactor,
            payerOccupationFactor
          )
        } else {
          return { planCode: '' }
        }
      case values.OPTION_ANY_VALUE_PLAN_GROUP:
        return getSelectedRiderPlanByInsuredAgeWithPlanGroup(rider, insuredAge)
      case values.OPTION_FIXED_VALUE:
      case values.OPTION_ANY_VALUE:
      case values.OPTION_FIXED_VALUE_HIDE_SUMASSURED:
      default:
        return { planCode: '' }
    }
  }

  const calculateMaximumPayerAge = (riderCode, insuredAge, yearsOfPayment, coveragePeriod) => {
    if (riderCode === 'WPD' || riderCode === 'WPDD')
      return riders[riderCode].calculateMaximumPayerAge(yearsOfPayment, coveragePeriod)
    else if (values.RIDER_PB.includes(riderCode)) return riders[riderCode].calculateMaximumPayerAge()

    return { value: 0, unit: 'year' }
  }

  const getRiderSumAssured = (
    rider,
    basicSumAssured,
    basicPremium,
    basicPlan,
    userAgentType = defaults.DEFAULT_AGENT_TYPE,
    totalPremium = 0,
    sumAssuredContract
  ) => {
    if (!isImplementedRider(rider)) return 0

    const riderOptionType = get('options.0.type')(rider)
    const riderOptionSource = get('options.0.source')(rider)
    const riderOptionValue = get('options.0.values')(rider)
    switch (riderOptionType) {
      case values.OPTION_FIXED_VALUE:
      case values.OPTION_FIXED_VALUE_FROM_LIST:
      case values.OPTION_FIXED_VALUE_HIDE_SUMASSURED:
        if (riderOptionSource === values.SOURCE_BASIC_PREMIUM) {
          return basicPremium
        } else if (riderOptionSource === values.SOURCE_PRODUCT_PLAN) {
          return getRiderSumAssuredByProductPlan(rider, basicSumAssured, basicPlan, userAgentType)
        } else if (riderOptionSource === values.SOURCE_TOTAL_PREMIUM) {
          return totalPremium
        } else if (riderOptionSource === values.SOURCE_TOTAL_ANNUAL_PREMIUM) {
          return totalPremium
        } else if (riderOptionSource === values.SOURCE_BASIC_SUM_ASSURED) {
          return basicSumAssured
        } else if (riderOptionSource === values.SOURCE_PERCENT_BASIC_SUM_ASSURED) {
          let calculatedBasicSumAssured = basicSumAssured
          if (basicPlan !== undefined && basicPlan.category !== undefined && basicPlan.category === values.MRTA) {
            calculatedBasicSumAssured = sumAssuredContract || 0
          }
          return parseInt(calculatedBasicSumAssured * (riderOptionValue / 100))
        } else if (riderOptionSource === values.SOURCE_TOTAL_BASIC_ANNUAL_PREMIUM_WITH_LIMITED_RANGE) {
          return rider.sumAssured
        } else {
          return Math.min(basicSumAssured, rider.defaultSumAssured)
        }
      case values.OPTION_VALUE_FROM_LIST: {
        const riderSelectedPlan = _.get(rider, 'selectedPlan.planCode')
        let riderSumAssured = rider.defaultSumAssured
        if (!_.isEmpty(riderSelectedPlan)) {
          if (rider.code === 'MHP') {
            const packageRiderMHP = _.get(rider, 'packageRiders')
            const found = packageRiderMHP.find((rider) => {
              const thailandCodes = rider.codes?.Thailand
              return (
                thailandCodes?.deductible === riderSelectedPlan || thailandCodes?.full_coverage === riderSelectedPlan
              )
            })
            riderSumAssured = found ? found.defaultSumAssured : rider.defaultSumAssured
          } else {
            const riderOption = _.find(riderOptionValue, { planCode: riderSelectedPlan })
            riderSumAssured = riderOption.value ? riderOption.value : rider.defaultSumAssured
          }
        }
        return riderSumAssured
      }
      case values.OPTION_ANY_VALUE_PLAN_GROUP:
      default:
        return rider.sumAssured ? rider.sumAssured : rider.defaultSumAssured
    }
  }

  const calculateRiderPremium = (rider, dependencies) => {
    if (!isImplementedRider(rider)) return 0

    const isRiderCampaignDiscountActive = isCheckRiderCampaignDiscountActive(rider, dependencies.userAgentType)
    Object.assign(dependencies, { isRiderCampaignDiscountActive: isRiderCampaignDiscountActive })

    const riderPremium = riders[rider.code].calculateRiderPremium(dependencies, rider)
    return riderPremium
  }

  const queryPremiumRate = (rider, riderPremiumRates, queryDependencies) => {
    if (!isImplementedRider(rider)) return 0
    return riders[rider.code].queryPremiumRate(riderPremiumRates, queryDependencies)
  }

  const queryTaxRate = (rider, riderTaxRates, queryDependencies) => {
    if (typeof riders[rider.code].queryTaxRate === 'undefined')
      return {
        retire: 0,
        life: 0,
        health: 0,
      }
    return riders[rider.code].queryTaxRate(riderTaxRates, queryDependencies)
  }

  const _validateRiders = (
    riders,
    insured,
    payerAge,
    sumAssured,
    occupationFactors,
    payerOccupationFactors,
    selectedBasicPlan,
    payerRelation,
    loan,
    userAgentType,
    modelFactor
  ) => {
    const getRiderEligibilities = (rider, selectedRiders, payerRelation, userAgentType, modelFactor) => {
      //TODO should move to acculateRider
      const yearsOfPayment = getYearsOfPayment(
        rider,
        insured.age,
        payerAge,
        selectedBasicPlan.paymentPeriod,
        selectedBasicPlan.coveragePeriod,
        rider.selectedPlan
      )
      const maxPayerAge = calculateMaximumPayerAge(rider.code, insured.age, yearsOfPayment, rider.coveragePeriod.value)

      return {
        insured,
        payerAge,
        basicSumAssured: sumAssured,
        yearsOfPayment,
        riderCoveragePeriod: rider.coveragePeriod.value,
        selectedRiders,
        rider,
        maxPayerAge,
        occupationFactor: occupationFactors[rider.code],
        payerOccupationFactor: payerOccupationFactors[rider.code],
        basicPaymentPeriod: selectedBasicPlan.paymentPeriod,
        payerRelation: payerRelation,
        loan: loan,
        userAgentType: userAgentType,
        modelFactor,
      }
    }

    let selectedRiders = riders.filter(
      (rider) =>
        rider.isSelected &&
        isRiderSelectable(rider, getRiderEligibilities(rider, [], payerRelation, userAgentType, modelFactor))
    )
    let selectedRiderCodes = selectedRiders.map((riderPlan) => riderPlan.code)

    //if both WP and PB will consider PB as priority
    if (
      !_.isEqual(_.intersection([values.WPTPDCI, values.WPTPD], selectedRiderCodes), []) &&
      !_.isEqual(_.intersection(values.RIDER_PB, selectedRiderCodes), [])
    ) {
      selectedRiders = selectedRiders.filter((rider) => ![values.WPTPDCI, values.WPTPD].includes(rider.code))
      selectedRiderCodes = selectedRiders.map((riderPlan) => riderPlan.code)
    }

    const isSelectable = (rider, riderEligibilities) =>
      isCompatibleWithSelectedRiders(rider, selectedRiderCodes) && isRiderSelectable(rider, riderEligibilities)

    return riders.map((rider) => {
      if (!isCheckRiderCampaignDiscountActive(rider, userAgentType)) {
        if (rider.hasOwnProperty('fullPremium')) {
          delete rider.fullPremium
        }
      }
      const yearsOfCoverage = getYearsOfCoverage(
        rider,
        insured.age,
        payerAge,
        selectedBasicPlan.paymentPeriod,
        selectedBasicPlan.coveragePeriod,
        rider.selectedPlan
      )
      const riderEligibilities = getRiderEligibilities(rider, selectedRiders, payerRelation, userAgentType, modelFactor)
      const isRiderSelectable = isSelectable(rider, riderEligibilities)
      const errors = getValidationMessages(rider, rider.sumAssured, riderEligibilities, selectedRiders)
      return {
        ...rider,
        isSelected: rider.isSelected,
        isSelectable: isRiderSelectable,
        errors,
        yearsOfCoverage: yearsOfCoverage,
        yearsOfPayment: riderEligibilities.yearsOfPayment,
      }
    })
  }

  const validateRiders = async (
    riders,
    insured,
    payerAge,
    sumAssured,
    natureOfDutyCodes = [],
    payerNatureOfDutyCodes = [],
    displayProductQuery,
    payerRelation = '',
    loan,
    userAgentType = 'AGENT',
    modelFactor
  ) => {
    const occupationFactors = await insuredOccupationService.getRiskiestOccupationFactorForRiders(
      displayProductQuery,
      natureOfDutyCodes,
      riders
    )
    const payerOccupationFactors = await insuredOccupationService.getRiskiestOccupationFactorForRiders(
      displayProductQuery,
      payerNatureOfDutyCodes,
      riders
    )
    const basicPlan = await displayProductService.getBasicPlan(displayProductQuery)
    return _validateRiders(
      riders,
      insured,
      payerAge,
      sumAssured,
      occupationFactors,
      payerOccupationFactors,
      basicPlan,
      payerRelation,
      loan,
      userAgentType,
      modelFactor
    )
  }

  const getAccurateRiders = async (props) => {
    const {
      riders,
      basicSumAssured,
      displayProductQuery,
      natureOfDutyCodes,
      payerNatureOfDutyCodes,
      userAgentType,
      insured,
      payerAge,
    } = props

    const occupationFactors = await insuredOccupationService.getRiskiestOccupationFactorForRiders(
      displayProductQuery,
      natureOfDutyCodes,
      riders
    )
    const payerOccupationFactors = await insuredOccupationService.getRiskiestOccupationFactorForRiders(
      displayProductQuery,
      payerNatureOfDutyCodes,
      riders
    )
    const basicPlan = await displayProductService.getBasicPlan(displayProductQuery)
    return riders.map((rider) => {
      const occupationFactor = occupationFactors[rider.code]
      const payerOccupationFactor = payerOccupationFactors[rider.code]
      const yearsOfCoverage = getToggles().ENABLE_FIXED_WPD_CALCULATION
        ? getYearsOfCoverage(
            rider,
            insured.age,
            payerAge,
            basicPlan.paymentPeriod,
            basicPlan.coveragePeriod,
            rider.selectedPlan
          )
        : rider.yearsOfCoverage
      return {
        ...rider,
        yearsOfCoverage,
        selectedPlan: getSelectedRiderPlan(
          rider,
          rider.selectedPlan,
          basicPlan,
          basicSumAssured,
          userAgentType,
          occupationFactor,
          payerOccupationFactor,
          insured.age
        ),
        riderOccupationFactor: occupationFactor.toString(),
        riderPayerOccupationFactor: payerOccupationFactor.toString(),
      }
    })
  }

  return {
    getYearsOfCoverage,
    getYearsOfPayment,
    getSelectedRiderPlan,
    calculateMaximumPayerAge,
    getRiderSumAssured,
    calculateRiderPremium,
    queryPremiumRate,
    queryTaxRate,
    _validateRiders,
    validateRiders,
    getAccurateRiders,
    getSelectedRiderPlanByInsuredAgeWithPlanGroup,
    isAgeAllowed,
  }
}
